import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ExternalLink, KeyRound, Mail, MailCheck, ShieldCheck, Zap } from 'lucide-react';
import type { MagicLinkRequestResponse } from '../../shared/api';
import { isValidEmail } from '../../shared/utils';
import { AuthLayout } from '../components/layout';
import { Alert, Button, CopyButton, Field, Input } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useSessionStore } from '../stores/session';

/** Google's mark, inline so the button works with no network access. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.63h6.46a5.52 5.52 0 0 1-2.39 3.62v3h3.86c2.26-2.08 3.56-5.15 3.56-8.8Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.08 7.94-2.91l-3.87-3a7.2 7.2 0 0 1-10.72-3.78h-4v3.09A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.35 14.3a7.19 7.19 0 0 1 0-4.6V6.62h-4a12.01 12.01 0 0 0 0 10.77l4-3.09Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.43-3.43C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.35 6.62l4 3.09A7.16 7.16 0 0 1 12 4.75Z"
      />
    </svg>
  );
}

/**
 * The `?error=` codes `/api/auth/*` redirects back with. Each one has a
 * different fix, so each one gets its own sentence — "try again" tells a person
 * whose invite expired nothing at all.
 */
const AUTH_ERRORS: Record<string, { title: string; body: string }> = {
  invalid_link: {
    title: 'That sign-in link has expired',
    body: 'Magic links are single-use and last 15 minutes. Request a fresh one below.',
  },
  invite_required: {
    title: 'No account for that email',
    body: 'Murmur is invite-only. Ask a workspace owner to invite this address, then use the link they send.',
  },
  google_unavailable: {
    title: 'Google sign-in is not configured',
    body: 'This deployment has no Google OAuth credentials set. Use a magic link instead.',
  },
  google_failed: {
    title: 'Google sign-in did not complete',
    body: 'The handshake with Google failed or was cancelled. Try again, or use a magic link.',
  },
  access_denied: {
    title: 'Sign-in was cancelled',
    body: 'You closed the provider before it finished. Nothing was changed.',
  },
};

export function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const authMethods = useSessionStore((state) => state.authMethods);
  const setupRequired = useSessionStore((state) => state.setupRequired);
  const refresh = useSessionStore((state) => state.refresh);

  const redirectTo =
    (location.state as { from?: string } | null)?.from ?? searchParams.get('redirect') ?? '/';
  const inviteToken = searchParams.get('inviteToken') ?? undefined;
  const authError = searchParams.get('error');

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sent, setSent] = useState<MagicLinkRequestResponse | null>(null);

  const requestLink = useMutation({
    mutationFn: () =>
      api.auth.requestMagicLink({
        email: email.trim(),
        redirect: redirectTo,
        ...(inviteToken ? { inviteToken } : {}),
      }),
    onSuccess: (response) => setSent(response),
  });

  const devLogin = useMutation({
    mutationFn: () => api.auth.devLogin({ email: email.trim() }),
    onSuccess: async () => {
      await refresh();
      navigate(redirectTo, { replace: true });
    },
  });

  const submitMagicLink = () => {
    const value = email.trim();
    if (!isValidEmail(value)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(null);
    requestLink.mutate();
  };

  const requestError = requestLink.error instanceof ApiError ? requestLink.error : null;
  const devError = devLogin.error instanceof ApiError ? devLogin.error : null;

  if (setupRequired) {
    return (
      <AuthLayout
        eyebrow="First run"
        title="This deployment needs setting up"
        subtitle="No account exists yet. Claim the first one with the setup token to create your superadmin user and workspace."
      >
        <Button
          fullWidth
          size="lg"
          leftIcon={<KeyRound className="size-4" />}
          onClick={() => navigate('/setup')}
        >
          Start setup
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Murmur is invite-only — use the address your workspace invited."
      footer={
        <>
          Have an invitation link?{' '}
          <Link to="/accept-invite" className="font-medium text-accent hover:underline">
            Accept your invite
          </Link>
        </>
      }
    >
      <div className="space-y-5">
        {authError && (
          <Alert
            tone={authError === 'invite_required' ? 'warning' : 'danger'}
            title={AUTH_ERRORS[authError]?.title ?? 'Sign-in failed'}
          >
            {AUTH_ERRORS[authError]?.body ??
              'That sign-in attempt could not be completed. Try a magic link instead.'}
          </Alert>
        )}

        <AnimatePresence mode="wait" initial={false}>
          {sent ? (
            <motion.div
              key="sent"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              <div className="flex flex-col items-center gap-3 py-2 text-center">
                <span className="flex size-11 items-center justify-center rounded-full bg-accent-soft text-accent">
                  <MailCheck className="size-5" aria-hidden />
                </span>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-fg">Check your inbox</p>
                  <p className="text-sm text-muted">{sent.message}</p>
                </div>
              </div>

              {sent.devLink && (
                <Alert
                  tone="info"
                  title="Email delivery is not configured"
                  icon={<Zap />}
                >
                  <p>Use this one-time sign-in link — it is shown here instead of being emailed.</p>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-surface px-2 py-1 font-mono text-[11px] text-fg">
                      {sent.devLink}
                    </code>
                    <CopyButton value={sent.devLink} />
                  </div>
                  <Button
                    className="mt-2"
                    size="sm"
                    rightIcon={<ExternalLink className="size-3.5" />}
                    onClick={() => {
                      window.location.href = sent.devLink as string;
                    }}
                  >
                    Open sign-in link
                  </Button>
                </Alert>
              )}

              <Button
                variant="ghost"
                fullWidth
                onClick={() => {
                  setSent(null);
                  requestLink.reset();
                }}
              >
                Use a different email
              </Button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                submitMagicLink();
              }}
            >
              {authMethods.google && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    fullWidth
                    leftIcon={<GoogleMark />}
                    onClick={() => {
                      window.location.href = api.auth.googleStartUrl(redirectTo, inviteToken);
                    }}
                  >
                    Continue with Google
                  </Button>
                  {authMethods.magicLink && (
                    <div className="flex items-center gap-3 py-1">
                      <span className="h-px flex-1 bg-border" />
                      <span className="text-[11px] uppercase tracking-wide text-subtle">or</span>
                      <span className="h-px flex-1 bg-border" />
                    </div>
                  )}
                </>
              )}

              {(authMethods.magicLink || authMethods.devLogin) && (
                <Field
                  label="Work email"
                  htmlFor="login-email"
                  error={emailError ?? requestError?.message ?? null}
                  hint={
                    !authMethods.magicLink
                      ? 'Dev mode signs you in as this address without an email round-trip.'
                      : authMethods.emailDelivery
                        ? 'We email you a link that signs you in — no password to remember.'
                        : 'Email delivery is not configured, so the link appears right here.'
                  }
                >
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    inputSize="lg"
                    placeholder="you@company.com"
                    leadingIcon={<Mail />}
                    value={email}
                    invalid={Boolean(emailError)}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (emailError) setEmailError(null);
                    }}
                  />
                </Field>
              )}

              {authMethods.magicLink && (
                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  loading={requestLink.isPending}
                  loadingLabel="Sending your sign-in link"
                  rightIcon={<ArrowRight className="size-4" />}
                >
                  Email me a sign-in link
                </Button>
              )}

              {authMethods.devLogin && (
                <div className="space-y-2 rounded-lg border border-dashed border-border bg-elevated/40 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-subtle">
                    Dev mode
                  </p>
                  <p className="text-xs text-muted">
                    Skip the email round-trip and sign in as the address above.
                  </p>
                  {devError && <p className="text-xs text-danger">{devError.message}</p>}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    fullWidth
                    loading={devLogin.isPending}
                    disabled={!isValidEmail(email.trim())}
                    onClick={() => devLogin.mutate()}
                  >
                    Instant dev sign-in
                  </Button>
                </div>
              )}

              {!authMethods.magicLink && !authMethods.google && !authMethods.devLogin && (
                <Alert tone="warning" title="No sign-in method is configured">
                  Set <code className="font-mono">RESEND_API_KEY</code> for magic links, or
                  <code className="font-mono"> GOOGLE_OAUTH_CLIENT_ID</code> and
                  <code className="font-mono"> GOOGLE_OAUTH_CLIENT_SECRET</code> for Google sign-in.
                </Alert>
              )}
            </motion.form>
          )}
        </AnimatePresence>

        <p className="flex items-start gap-2 border-t border-border pt-4 text-xs text-subtle">
          <ShieldCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            There is no self-serve sign-up. Accounts are created by an invitation from a workspace
            owner, or by the first-run setup.
          </span>
        </p>
      </div>
    </AuthLayout>
  );
}

export default LoginPage;
